import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Envelope } from '@phosphor-icons/react';
import { mapAuthError } from '@mybusinessmate/api-client';
import { signUpInputSchema, type SignUpInput } from '@mybusinessmate/schema';
import { AuthHeading } from '../../../../../components/auth/auth-heading';
import { TermsLinks } from '../../../../../components/auth/terms-links';
import { Banner } from '../../../../../components/ui/banner';
import { Button } from '../../../../../components/ui/button';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { Input, PasswordInput } from '../../../../../components/ui/input';
import { textLinkClass } from '../../../../../components/ui/text-link';
import { authClient } from '../../../../../lib/api';
import { requireAssessment } from '../../../../../lib/guards';
import { useOnboardingStore } from '../../../../../lib/onboarding-store';
import { passwordStrength, STRENGTH_COPY } from '../../../../../lib/password-strength';

export const Route = createFileRoute('/subdomain/app/_auth/sign-up/email')({
  // Creating an account comes after the assessment; opened directly, resume the assessment instead.
  beforeLoad: () => requireAssessment(),
  component: SignUpEmailPage,
});

/** Better Auth requires a name; the design only asks for email + password, so start from the email's local part. */
const nameFromEmail = (email: string) => email.split('@')[0] ?? email;

function SignUpEmailPage() {
  const navigate = useNavigate();
  const setPendingEmail = useOnboardingStore((s) => s.setPendingEmail);

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SignUpInput>({
    resolver: zodResolver(signUpInputSchema),
    mode: 'onBlur',
    defaultValues: { email: '', password: '', acceptedTerms: false },
  });

  const password = useWatch({ control, name: 'password' });
  const strength = passwordStrength(password);

  const onSubmit = handleSubmit(async ({ email, password }) => {
    const { error } = await authClient.signUp.email({ email, password, name: nameFromEmail(email) });
    const mapped = mapAuthError(error);
    if (mapped) {
      if (mapped.kind === 'duplicate_email') {
        setError('email', { message: 'This email already has an account. Sign in instead?' });
      } else if (mapped.kind === 'validation' && mapped.fieldErrors[0]) {
        setError(mapped.fieldErrors[0].field as keyof SignUpInput, { message: mapped.message });
      } else {
        setError('root', { message: mapped.message });
      }
      return;
    }
    setPendingEmail(email);
    await navigate({ to: '/subdomain/app/verify-email' });
  });

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-(--mbm-space-6)">
      <AuthHeading title="Create your account" subtitle="Use your business email if you have one." />

      {errors.root ? <Banner tone="error">{errors.root.message}</Banner> : null}

      <Input
        label="Email"
        type="email"
        autoComplete="email"
        leadingIcon={Envelope}
        error={errors.email?.message}
        {...register('email')}
      />
      <PasswordInput
        label="Password"
        autoComplete="new-password"
        error={errors.password?.message}
        helperText={STRENGTH_COPY[strength] || undefined}
        {...register('password')}
      />

      <Controller
        control={control}
        name="acceptedTerms"
        render={({ field }) => (
          <Checkbox
            checked={field.value}
            onChange={(e) => field.onChange(e.target.checked)}
            onBlur={field.onBlur}
            error={errors.acceptedTerms?.message}
            label={
              <span>
                I agree to the <TermsLinks />
              </span>
            }
          />
        )}
      />

      <Button type="submit" fullWidth loading={isSubmitting}>
        Create account
      </Button>

      <p className="text-center text-body-small text-text-secondary">
        Already have an account?{' '}
        <Link to="/subdomain/app/sign-in" className={textLinkClass}>
          Sign in
        </Link>
      </p>
    </form>
  );
}
